/* eslint-disable @next/next/no-img-element */
import { ArrowLeft, MailSearch } from 'lucide-react'
import Link from 'next/link'
import { useEffect, useState } from 'react'

import { NON_FSDS_DEFAULT_LOGO_URL } from '@/configs/non-fsds-config'
import { TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'
import authService from '@/services/auth.service'

export interface NonFsdsVerifyAccountSectionProps {
  title: string
  subtitle: string
  logoUrl: string
  resendButtonText: string
  backLinkText: string
  brandHref?: string
  signInUrl?: string
}

export default function NonFsdsVerifyAccountSection({
  title = 'Verify your account',
  subtitle = 'We sent a verification link to your email. If you do not see anything, please click the button below.',
  logoUrl = NON_FSDS_DEFAULT_LOGO_URL,
  resendButtonText = 'Resend verification email',
  backLinkText = 'Back to sign in',
  brandHref = TEMPLATE_DEFAULT_URLS.home,
  signInUrl = TEMPLATE_DEFAULT_URLS.signIn,
}: NonFsdsVerifyAccountSectionProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [feedback, setFeedback] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)

  useEffect(() => {
    const storedEmail = localStorage.getItem('pendingVerifyEmail')
    setEmail(storedEmail)
  }, [])

  const handleResend = async () => {
    setIsLoading(true)
    setFeedback(null)
    setError(null)

    try {
      const res = await authService.resendEmailVerification(email as string)
      if (res) setFeedback('Verification email sent! Please check your inbox.')
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('Something went wrong.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden bg-slate-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-blue-100 via-transparent to-transparent opacity-40" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
      <div className="pointer-events-none absolute left-10 top-20 h-72 w-72 rounded-full bg-blue-300/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-md rounded-3xl border border-slate-100/50 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-12">
        <div className="mb-8 flex justify-center">
          <Link href={brandHref} className="inline-block transition-transform duration-200 hover:scale-105 active:scale-95">
            <img src={logoUrl} alt="Logo" className="h-10 w-auto" />
          </Link>
        </div>

        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-6 rounded-full bg-blue-50 p-4">
            <MailSearch className="h-10 w-10 text-blue-500" />
          </div>
          <h2 className="mb-2 text-2xl font-bold text-slate-900">{title}</h2>
          <p className="mx-auto max-w-xs text-sm text-slate-500">{subtitle}</p>
        </div>

        <div className="flex w-full flex-col items-center space-y-2">
          <button
            onClick={handleResend}
            disabled={isLoading || !email}
            className="flex w-full items-center justify-center rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-blue-700 hover:shadow-blue-600/25 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:opacity-50"
          >
            {isLoading ? 'Resending…' : resendButtonText}
          </button>

          <div className="mt-4 min-h-[1.5rem] w-full text-center">
            {feedback && <p className="text-sm font-medium text-green-600">{feedback}</p>}
            {error && <p className="text-sm font-medium text-blue-600">{error}</p>}
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link
            href={signInUrl}
            className="inline-flex items-center justify-center rounded-md text-sm font-semibold text-slate-500 transition-colors hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {backLinkText}
          </Link>
        </div>
      </div>
    </div>
  )
}
